/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import { getElementToFocus } from "selectors/accessibilitySelectors";
import { setCaretAtStartOfRect } from "utils/CaretModeUtils";
import React, { useEffect, useRef } from "react";

export const useRefToSetFocus = (shouldFocus, resetShouldFocus) => {
  const ref = useRef(null);
  useEffect(() => {
    if (shouldFocus) {
      if (document.activeElement !== ref.current) {
        ref.current.focus();
        ref.current.addEventListener("blur", resetShouldFocus, { once: true });
      }
    } else if (document.activeElement === ref.current) ref.current.blur();
  }, [shouldFocus]);
  return ref;
};

const withFocusability = Component => {
  const mapStoresToProps = (stores, ownProps) => {
    const elementToFocus = getElementToFocus(stores);
    const shouldFocus =
      elementToFocus && elementToFocus.pageIndex === ownProps.pageIndex && elementToFocus.id === ownProps.nodeId;
    return { shouldFocus, resetShouldFocus: () => stores.accessibilityStore.setElementToFocus(null) };
  };
  return inject(mapStoresToProps)(Component);
};

const withCaretOnFocus = Component => props => (
  <Component
    onFocus={() => props.contentRect && setCaretAtStartOfRect(props.contentRect, props.pageIndex)}
    {...props}
  />
);

export { withFocusability, withCaretOnFocus };
